import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Handshake, ShieldCheck, ExternalLink, PartyPopper } from 'lucide-react';
import { company } from '../../mock';
import { useI18n } from '../../i18n';

const copy = {
  en: {
    badge: 'Partnership Announcement',
    title: 'Welcome to Hipro Technologies',
    subtitle: 'We’re proud to announce our authorized partnership with',
    body: 'Together, Hipro Technologies and Sasol are joining forces to accelerate digital transformation, cloud engineering and AI-led operations across global markets.',
    ctaVisit: 'Visit Sasol',
    ctaExplore: 'Explore Hipro',
  },
  it: {
    badge: 'Annuncio partnership',
    title: 'Benvenuti in Hipro Technologies',
    subtitle: 'Siamo orgogliosi di annunciare la nostra partnership autorizzata con',
    body: 'Insieme, Hipro Technologies e Sasol accelerano la trasformazione digitale, il cloud e le operazioni guidate da AI nei mercati globali.',
    ctaVisit: 'Visita Sasol',
    ctaExplore: 'Scopri Hipro',
  },
  hi: {
    badge: 'साझेदारी की घोषणा',
    title: 'Hipro Technologies में आपका स्वागत है',
    subtitle: 'हमें अधिकृत साझेदारी की घोषणा करते हुए गर्व है',
    body: 'Hipro Technologies और Sasol मिलकर वैश्विक बाज़ारों में डिजिटल ट्रांसफ़ॉर्मेशन, क्लाउड इंजीनियरिंग और AI संचालन को तेज़ कर रहे हैं।',
    ctaVisit: 'Sasol देखें',
    ctaExplore: 'Hipro देखें',
  },
};

const WelcomeModal = () => {
  const { lang } = useI18n();
  const c = copy[lang] || copy.en;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const seen = sessionStorage.getItem('hipro_welcome_seen');
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const close = () => {
    sessionStorage.setItem('hipro_welcome_seen', '1');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-none shadow-2xl bg-slate-950 text-white">
        <DialogTitle className="sr-only">{c.title}</DialogTitle>
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(500px 240px at 20% 0%, rgba(249,115,22,0.35), transparent 60%), radial-gradient(500px 240px at 90% 100%, rgba(59,130,246,0.30), transparent 60%)',
          }}
        />
        <div className="relative p-7">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 text-[11px] font-bold uppercase tracking-widest px-3 py-1">
            <PartyPopper className="h-3.5 w-3.5" /> {c.badge}
          </div>
          <h3 className="mt-4 text-2xl font-extrabold">{c.title}</h3>
          <p className="mt-2 text-sm text-slate-300">
            {c.subtitle} <b>Sasol</b>.
          </p>

          <div className="mt-6 grid grid-cols-5 items-center gap-3">
            <div className="col-span-2 rounded-xl bg-white p-3 flex flex-col items-center gap-1.5">
              <img src={company.logo} alt="Hipro Technologies" className="h-10 w-auto" />
              <div className="text-[11px] font-bold text-slate-900">Hipro Technologies</div>
            </div>
            <div className="col-span-1 flex justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
                <Handshake className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="col-span-2 rounded-xl bg-white p-3 flex flex-col items-center gap-1.5">
              <div className="h-10 flex items-center justify-center">
                <img
                  src="https://logo.clearbit.com/sasol.com?size=200"
                  alt="Sasol"
                  className="max-h-10 w-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fb = e.currentTarget.nextSibling;
                    if (fb) fb.style.display = 'block';
                  }}
                />
                <div style={{ display: 'none' }} className="text-2xl font-black tracking-tight">
                  <span className="text-[#0069b4]">sa</span>
                  <span className="text-[#ed1c24]">s</span>
                  <span className="text-[#0069b4]">ol</span>
                </div>
              </div>
              <div className="text-[11px] font-bold text-slate-900">Sasol</div>
            </div>
          </div>

          <p className="mt-5 text-sm text-slate-300 leading-relaxed">{c.body}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="https://sasol.com/" target="_blank" rel="noreferrer noopener" onClick={close}>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11 px-5">
                {c.ctaVisit} <ExternalLink className="h-4 w-4 ml-1.5" />
              </Button>
            </a>
            <Button
              onClick={close}
              variant="outline"
              className="bg-transparent border-white/20 text-white hover:bg-white/10 h-11 px-5"
            >
              <ShieldCheck className="h-4 w-4 mr-1.5 text-emerald-400" /> {c.ctaExplore}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;
